import { useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

export default function FillBlankExercise({
  exercise,
  submittingLearningExercise,
  submitLearningExercise,
  styles,
}) {
  const { language } = useLanguage();

  // =========================================================
  // Canonical data
  // These remain the source of truth for validation.
  // =========================================================
  const sentence =
    exercise.exercise_data?.sentence ||
    exercise.prompt ||
    "";

  const acceptedAnswers =
    exercise.exercise_data?.answers || [];

  // =========================================================
  // Display-only ES / RU translations
  // =========================================================
  const translatedData =
    exercise.translated_exercise_data || {};

  const translatedPrompt =
    language === "es" || language === "ru"
      ? exercise.translated_prompt || null
      : null;

  const translatedSentence =
    language === "es" || language === "ru"
      ? translatedData.sentence || null
      : null;

  // =========================================================
  // Separate legacy mixed English + Chinese display text
  // =========================================================
  const splitEnglishChinese = (text = "") => {
    const firstChineseIndex = text.search(
      /[\u3400-\u4DBF\u4E00-\u9FFF]/
    );

    if (firstChineseIndex === -1) {
      return {
        english: text.trim(),
        chinese: null,
      };
    }

    return {
      english: text
        .slice(0, firstChineseIndex)
        .trim(),

      chinese: text
        .slice(firstChineseIndex)
        .trim(),
    };
  };

  const promptParts =
    splitEnglishChinese(exercise.prompt || "");

  const sentenceParts =
    splitEnglishChinese(sentence);

  const englishPrompt = promptParts.english;
  const chinesePrompt = promptParts.chinese;

  const englishSentence = sentenceParts.english;
  const chineseSentence = sentenceParts.chinese;

  // =========================================================
  // State
  // =========================================================
  const [answer, setAnswer] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  // =========================================================
  // Canonical English validation
  // =========================================================
  const normalize = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const handleSubmit = async () => {
    if (!answer.trim()) return;

    const normalizedAnswer = normalize(answer);

    // IMPORTANT:
    // Validation remains against canonical English answers only.
    const correct = acceptedAnswers.some(
      (accepted) =>
        normalize(accepted) === normalizedAnswer
    );

    const success =
      await submitLearningExercise({
        exerciseId: exercise.id,
        answer: {
          entered_answer: answer,
        },
        isCorrect: correct,
      });

    if (!success) return;

    setIsCorrect(correct);
    setSubmitted(true);
  };

  const handleTryAgain = () => {
    setAnswer("");
    setSubmitted(false);
    setIsCorrect(false);
  };

  return (
    <div
      style={{
        ...styles.emptyBox,
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        Fill in the Blank
      </div>

      {/* =====================================================
          Prompt
          EN: English only
          ZH: English + Chinese
          ES/RU: English + selected translation
      ====================================================== */}

      {englishPrompt &&
        englishPrompt !== englishSentence && (
          <div style={styles.quizPath}>
            {englishPrompt}
          </div>
        )}

      {language === "zh" &&
        chinesePrompt &&
        chinesePrompt !== chineseSentence && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {chinesePrompt}
          </div>
        )}

      {(language === "es" ||
        language === "ru") &&
        translatedPrompt &&
        translatedPrompt !==
          translatedSentence && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {translatedPrompt}
          </div>
        )}

      {/* =====================================================
          Sentence
      ====================================================== */}

      {englishSentence && (
        <div
          style={{
            marginTop: "14px",
            fontSize: "17px",
            lineHeight: "1.6",
          }}
        >
          {englishSentence}
        </div>
      )}

      {language === "zh" &&
        chineseSentence && (
          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              lineHeight: "1.6",
            }}
          >
            {chineseSentence}
          </div>
        )}

      {(language === "es" ||
        language === "ru") &&
        translatedSentence && (
          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              lineHeight: "1.6",
            }}
          >
            {translatedSentence}
          </div>
        )}

      {/* =====================================================
          Answer input
          User must answer in canonical English.
      ====================================================== */}

      <input
        type="text"
        placeholder="Type your answer in English..."
        value={answer}
        disabled={submitted}
        onChange={(e) =>
          setAnswer(e.target.value)
        }
        style={{
          ...styles.loginInput,
          marginTop: "14px",
        }}
      />

      {/* =====================================================
          Submit / Result
      ====================================================== */}

      {!submitted ? (
        <button
          style={{
            ...styles.smallPrimaryButton,
            marginTop: "14px",
          }}
          disabled={
            submittingLearningExercise ||
            !answer.trim()
          }
          onClick={handleSubmit}
        >
          {submittingLearningExercise
            ? "Submitting..."
            : "Submit Answer"}
        </button>
      ) : (
        <div style={{ marginTop: "16px" }}>
          {isCorrect ? (
            <div
              style={{
                color: "#16a34a",
                fontWeight: "bold",
              }}
            >
              Correct
            </div>
          ) : (
            <div style={{ color: "#dc2626" }}>
              <div
                style={{
                  fontWeight: "bold",
                }}
              >
                Incorrect
              </div>

              {acceptedAnswers.length > 0 && (
                <div
                  style={{
                    marginTop: "6px",
                  }}
                >
                  Correct answer:{" "}
                  <strong>
                    {acceptedAnswers[0]}
                  </strong>
                </div>
              )}
            </div>
          )}

          <button
            style={{
              ...styles.smallSecondaryButton,
              marginTop: "10px",
            }}
            onClick={handleTryAgain}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}